import { create } from "zustand";
import api from "./api";

const COMPANY_KEY = "ihpg_company_id";

export const useAuth = create((set, get) => ({
    user: null,
    companies: [],
    companyId: localStorage.getItem(COMPANY_KEY) || null,
    loading: true,

    setCompany: (companyId) => {
        if (companyId) localStorage.setItem(COMPANY_KEY, companyId);
        else localStorage.removeItem(COMPANY_KEY);
        set({ companyId });
    },

    login: async (email, password, rememberMe) => {
        const { data } = await api.post("/auth/login", {
            email,
            password,
            remember_me: !!rememberMe,
        });
        // Two-step MFA: caller must handle mfa_required by prompting for code and calling mfaChallenge
        if (data.mfa_required) return data;
        return get()._finishLogin(data);
    },

    mfaChallenge: async (mfaSessionToken, code, isBackup) => {
        const { data } = await api.post("/auth/mfa/challenge", {
            mfa_session_token: mfaSessionToken,
            code,
            is_backup: !!isBackup,
        });
        return get()._finishLogin(data);
    },

    _finishLogin: (data) => {
        const isSuper = data.user?.role_global === "super_admin";
        const first = data.companies?.[0]?.id || null;
        const currentCompany = get().companyId;
        const isValidCompany =
            currentCompany === "__all__" && isSuper
                ? true
                : data.companies?.some((c) => c.id === currentCompany);
        const companyId = isValidCompany
            ? currentCompany
            : (isSuper ? "__all__" : first);
        if (companyId) localStorage.setItem(COMPANY_KEY, companyId);
        set({
            user: data.user,
            companies: data.companies || [],
            companyId,
            loading: false,
        });
        return data;
    },

    logout: async () => {
        try {
            await api.post("/auth/logout");
        } catch (_) { }
        localStorage.removeItem(COMPANY_KEY);
        set({ user: null, companies: [], companyId: null, loading: false });
    },

    refreshMe: async () => {
        try {
            const { data } = await api.get("/auth/me");
            const isSuper = data.user?.role_global === "super_admin";
            const currentCompany = get().companyId;
            const isValidCompany =
                currentCompany === "__all__" && isSuper
                    ? true
                    : data.companies?.some((c) => c.id === currentCompany);
            const companyId = isValidCompany
                ? currentCompany
                : (isSuper ? "__all__" : data.companies?.[0]?.id || null);
            if (companyId) localStorage.setItem(COMPANY_KEY, companyId);
            set({
                user: data.user,
                companies: data.companies || [],
                companyId,
                loading: false,
            });
            return data;
        } catch (e) {
            set({ user: null, companies: [], companyId: null, loading: false });
            return null;
        }
    },

    // Helpers
    isSuperAdmin: () => get().user?.role_global === "super_admin",
    currentCompanyRole: () => {
        const cid = get().companyId;
        const found = get().companies.find((c) => c.id === cid);
        return found?.role || null;
    },
    canAdmin: () => {
        const u = get().user;
        if (!u) return false;
        if (u.role_global === "super_admin") return true;
        return get().currentCompanyRole() === "amministratore";
    },
    canAnalyze: () => {
        const role = get().currentCompanyRole();
        return get().isSuperAdmin() || role === "amministratore" || role === "analista";
    },
}));
